/**
 * The PocketBase client — the single source of auth and CRUD.
 *
 * Clients talk to PocketBase directly; its API rules enforce per-user isolation
 * at the data layer, so there is no server-side code path that could forget a
 * `WHERE owner = ...`. The FastAPI service is used only for aggregation and
 * export.
 */

import PocketBase from 'pocketbase'

import { toDateOnlyString } from '@/domain/dates'
import type { ItemInput, ItemRecord, UserRecord } from '@/domain/types'

const PB_URL = import.meta.env.VITE_PB_URL ?? 'http://127.0.0.1:8090'
const API_URL = import.meta.env.VITE_API_URL ?? ''

export const pb = new PocketBase(PB_URL)

// Reminders are sent by the worker, not the browser, so a stale realtime
// subscription is not worth the reconnect noise.
pb.autoCancellation(false)

export function currentUser(): UserRecord | null {
  const model = pb.authStore.record
  if (!model) return null
  return model as unknown as UserRecord
}

export function isLoggedIn(): boolean {
  return pb.authStore.isValid && Boolean(pb.authStore.record)
}

// ---- Auth -----------------------------------------------------------------

export type OAuthProvider = 'google' | 'apple'

export async function login(email: string, password: string): Promise<void> {
  await pb.collection('users').authWithPassword(email, password)
}

/**
 * Sign in (or register) via Google / Apple.
 *
 * Must be called directly from a user gesture (click). Do not `await` anything
 * before this in the same handler — Safari treats that as a non-gesture and
 * blocks the OAuth popup.
 *
 * PocketBase opens the provider, handles the redirect to
 * `/api/oauth2-redirect`, and creates the user on first success. The existing
 * onRecordCreate hook still applies defaults (timezone, digest hour, etc.).
 */
export async function loginWithOAuth(provider: OAuthProvider): Promise<void> {
  await pb.collection('users').authWithOAuth2({ provider })
}

export async function register(email: string, password: string): Promise<void> {
  await pb.collection('users').create({
    email,
    password,
    passwordConfirm: password,
    emailVisibility: false,
  })
  // Accounts cannot log in until verified (users.authRule = "verified = true"),
  // so the verification email is requested immediately.
  await pb.collection('users').requestVerification(email)
}

export async function requestVerification(email: string): Promise<void> {
  await pb.collection('users').requestVerification(email)
}

export async function confirmVerification(token: string): Promise<void> {
  await pb.collection('users').confirmVerification(token)
}

export async function requestPasswordReset(email: string): Promise<void> {
  await pb.collection('users').requestPasswordReset(email)
}

export function logout(): void {
  pb.authStore.clear()
}

/**
 * Permanently delete the signed-in account.
 *
 * PocketBase cascadeDelete on items.owner / notifications.owner removes the
 * user's records with the user row. deleteRule is "id = auth.id".
 */
export async function deleteAccount(): Promise<void> {
  const user = currentUser()
  if (!user) throw new Error('not signed in')
  await pb.collection('users').delete(user.id)
  pb.authStore.clear()
}

// ---- Items ----------------------------------------------------------------

/** All of the caller's items. The API rules make "of the caller" automatic. */
export async function listItems(): Promise<ItemRecord[]> {
  const records = await pb.collection('items').getFullList({ sort: 'expiry_date' })
  return records as unknown as ItemRecord[]
}

export async function getItem(id: string): Promise<ItemRecord> {
  return (await pb.collection('items').getOne(id)) as unknown as ItemRecord
}

/**
 * Create an item, stamping the owner from the session.
 *
 * The `owner` sent here is also what the createRule checks
 * (`@request.body.owner = @request.auth.id`), so a tampered value is rejected
 * by the server rather than trusted.
 */
export async function createItem(input: ItemInput): Promise<ItemRecord> {
  const user = currentUser()
  if (!user) throw new Error('not signed in')
  const body = buildBody({ ...input, owner: user.id })
  return (await pb.collection('items').create(body)) as unknown as ItemRecord
}

export async function updateItem(id: string, input: Partial<ItemInput>): Promise<ItemRecord> {
  return (await pb.collection('items').update(id, buildBody(input))) as unknown as ItemRecord
}

export async function deleteItem(id: string): Promise<void> {
  await pb.collection('items').delete(id)
}

/**
 * Build a request body.
 *
 * A plain object rather than FormData, which matters: FormData stringifies every
 * value, so the `reminder_offsets` JSON field and the booleans would arrive as
 * text. There are no file uploads, so there is never a reason to reach for it.
 */
function buildBody(input: Partial<ItemInput> & { owner?: string }): Record<string, unknown> {
  const normalised: Record<string, unknown> = { ...input }
  if (typeof normalised.expiry_date === 'string') {
    // Accept either "YYYY-MM-DD" from an <input type="date"> or a full stamp.
    normalised.expiry_date = normalised.expiry_date.slice(0, 10)
  } else if (normalised.expiry_date instanceof Date) {
    normalised.expiry_date = toDateOnlyString(normalised.expiry_date)
  }
  return normalised
}

// ---- Settings -------------------------------------------------------------

export async function updateSettings(patch: {
  timezone?: string
  digest_hour?: number
  default_offsets?: number[]
  email_reminders?: boolean
}): Promise<UserRecord> {
  const user = currentUser()
  if (!user) throw new Error('not signed in')
  return (await pb.collection('users').update(user.id, patch)) as unknown as UserRecord
}

// ---- FastAPI --------------------------------------------------------------

/**
 * Call the Python service, forwarding the PocketBase token.
 *
 * FastAPI cannot verify these tokens offline (PocketBase signs them HS256 with
 * a per-record key), so it validates them against PocketBase and caches the
 * result briefly.
 */
export async function callApi<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: pb.authStore.token,
    },
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new ApiError(response.status, detail || response.statusText)
  }
  return (await response.json()) as T
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Whether an error is the server's considered answer rather than a failure to
 * reach it — so a caller knows not to retry.
 *
 * Both error shapes carry a numeric `status`: ApiError above, and PocketBase's
 * ClientResponseError. A network failure or a 502 from nginx mid-deploy is not a
 * 4xx (the SDK reports 0 when the request never landed), so both remain
 * retryable, which is the point.
 */
export function isClientError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' && status >= 400 && status < 500
}

/** Turn a PocketBase or fetch error into something worth showing a user. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    try {
      const parsed = JSON.parse(error.message) as { detail?: string }
      if (parsed.detail) return parsed.detail
    } catch {
      // not JSON; fall through to the raw message
    }
    return error.message
  }
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { message?: string; data?: Record<string, { message?: string }> } }).response
    const fieldError = Object.values(response?.data ?? {}).find((d) => d?.message)
    if (fieldError?.message) return fieldError.message
    if (response?.message) return response.message
  }
  if (error instanceof Error) return error.message
  return 'Something went wrong'
}
